// frontend/src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/authService';
import Swal from 'sweetalert2';

const Login = ({ setUser }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();

    // เพิ่ม Effect สำหรับการเคลื่อนไหวพื้นหลัง
    useEffect(() => {
        const handleMouseMove = (e) => {
            const x = e.clientX / window.innerWidth;
            const y = e.clientY / window.innerHeight;

            document.documentElement.style.setProperty('--mouse-x', x);
            document.documentElement.style.setProperty('--mouse-y', y);
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const userData = await login(username, password);
            setUser(userData);

            // บันทึกชื่อผู้ใช้ถ้าเลือก "จดจำฉัน"
            if (rememberMe) {
                localStorage.setItem('rememberedUsername', username);
            } else {
                localStorage.removeItem('rememberedUsername');
            }

            // แสดง welcome animation
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener('mouseenter', Swal.stopTimer)
                    toast.addEventListener('mouseleave', Swal.resumeTimer)
                }
            });

            Toast.fire({
                icon: 'success',
                title: `ยินดีต้อนรับ ${userData.name}`
            });

            // Redirect based on role
            setTimeout(() => {
                if (userData.role === 'super_admin') {
                    navigate('/super-admin/dashboard');
                } else if (userData.role === 'admin') {
                    navigate('/admin/dashboard');
                } else if (userData.role === 'referee') {
                    navigate('/referee/dashboard');
                }
            }, 1000);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'เข้าสู่ระบบล้มเหลว',
                text: error.message || 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
                confirmButtonText: 'ตกลง',
                confirmButtonColor: '#3085d6',
                customClass: {
                    popup: 'animated fadeInDown faster'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    // โหลดชื่อผู้ใช้ที่บันทึกไว้
    useEffect(() => {
        const savedUsername = localStorage.getItem('rememberedUsername');
        if (savedUsername) {
            setUsername(savedUsername);
            setRememberMe(true);
        }
    }, []);

    return (
        <div className="login-page min-vh-100 d-flex align-items-center">
            <div className="login-background-animation"></div>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-10 col-lg-8 col-xl-6">
                        <div className="login-wrapper">
                            <div className="logo-section text-center mb-4">
                                <img
                                    src="https://person.ssk3.go.th/wp-content/uploads/2023/12/%E0%B8%95%E0%B8%A3%E0%B8%B2%E0%B9%80%E0%B8%82%E0%B8%95-3-removebg-preview.png"
                                    alt="SSK3 Logo"
                                    className="logo-image animate__animated animate__fadeIn"
                                />
                                <h2 className="mt-3 fw-bold text-info animate__animated animate__fadeIn animate__delay-1s">ระบบ SSK3 ITA</h2>
                                <div className="text-separator">
                                    <span className="animate__animated animate__fadeIn animate__delay-1s">
                                        ระบบการประเมินคุณธรรมและความโปร่งใสในการดำเนินงานของหน่วยงานภาครัฐ
                                    </span>
                                </div>
                            </div>

                            <div className="card login-card shadow animate__animated animate__fadeIn animate__delay-2s">
                                <div className="card-body p-4 p-md-5">
                                    <h4 className="card-title text-center mb-4 fw-bold">เข้าสู่ระบบ</h4>
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-4 position-relative">
                                            <div className="input-group input-group-lg">
                                                <span className="input-group-text bg-transparent border-end-0">
                                                    <i className="fas fa-user text-primary"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control border-start-0 ps-0"
                                                    placeholder="ชื่อผู้ใช้"
                                                    id="username"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    required
                                                    autoComplete="username"
                                                />
                                            </div>
                                            <label htmlFor="username" className="form-label visually-hidden">
                                                ชื่อผู้ใช้
                                            </label>
                                        </div>

                                        <div className="mb-4 position-relative">
                                            <div className="input-group input-group-lg">
                                                <span className="input-group-text bg-transparent border-end-0">
                                                    <i className="fas fa-lock text-primary"></i>
                                                </span>
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    className="form-control border-start-0 border-end-0 ps-0"
                                                    placeholder="รหัสผ่าน"
                                                    id="password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    required
                                                    autoComplete="current-password"
                                                />
                                                <span
                                                    className="input-group-text bg-transparent border-start-0 cursor-pointer"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-primary`}></i>
                                                </span>
                                            </div>
                                            <label htmlFor="password" className="form-label visually-hidden">
                                                รหัสผ่าน
                                            </label>
                                        </div>

                                        <div className="mb-4 form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="rememberMe"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="rememberMe">
                                                จดจำฉัน
                                            </label>
                                        </div>

                                        <div className="d-grid">
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-lg py-3"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        กำลังเข้าสู่ระบบ...
                                                    </>
                                                ) : (
                                                    <><i className="fas fa-sign-in-alt me-2"></i>เข้าสู่ระบบ</>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            <div className="mt-4 text-center text-white">
                                <small>
                                    &copy; {new Date().getFullYear()} สำนักงานเขตพื้นที่การศึกษาประถมศึกษาศรีสะเกษ เขต 3 <br />
                                    พัฒนาโดย <a href="https://kruwirat.com/index.php" target="_blank" rel="noopener noreferrer" className="text-white text-decoration-none">www.Kruwirat.com</a>
                                </small>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;